import React, { useEffect, useState } from "react";
import { ClipboardList, Play, UserX, ArrowRight, Flag } from "lucide-react";
import { apiGet, apiPost, unpackList } from "../../../Api/Client.js";
import { isPlayable, playHref, stamp } from "./CallPlayer.jsx";
import { scoreColor, shortDate } from "./callMeta.js";
import LoadFailed from "./LoadFailed.jsx";

// The weekly review queue — the ritual, not another report.
//
// The difference between a call-scoring tool that gets used and one that sits
// on a shelf is whether there is a standing list somebody opens before their
// one-to-ones. Everything else on this product answers a question you have to
// think to ask. This one is the answer to "who do I talk to this week", already
// on the screen, with the quote and the timestamp attached.
//
// Three rules it exists to hold:
//
//   ONE CALL PER REP. Not every bad call. A queue you cannot reach the bottom
//   of is a queue nobody starts, and the second-worst call of somebody having a
//   rough week is not the conversation to have.
//
//   THE QUOTE IS THE POINT. A row that said "Arjun, 3.4" would send a manager
//   into a one-to-one with a number and no evidence, which is the meeting that
//   turns into an argument. The sentence and the timestamp turn it into
//   something the two of them can look at together.
//
//   SILENCE IS NOT GOOD NEWS. A rep who made no calls at all does not simply
//   drop off the list — they get their own section. A queue that only ranks
//   people who called is a queue that quietly rewards going quiet, which is the
//   same trap the patterns panel's `resolved` denominator guard exists for.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

function QueueRow({ row, onOpen }) {
  const focus = row.focus;
  const href = playHref(row.call_link, focus?.at_seconds);

  return (
    <div style={{ padding: "13px 0", borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
        <div style={{ minWidth: 0 }}>
          <strong style={{ fontSize: 13.5, color: "var(--c-900)" }}>{row.employee_name}</strong>
          <span style={{ fontSize: 10, ...muted, marginLeft: 7, textTransform: "uppercase",
                         letterSpacing: ".05em", padding: "2px 7px", borderRadius: 999,
                         background: "var(--c-100, rgba(120,120,120,.12))" }}>
            {row.role}
          </span>
          <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>
            {row.company_name ? <strong style={{ color: "var(--c-700)" }}>{row.company_name}</strong> : null}
            {row.company_name ? " · " : ""}{shortDate(row.call_date, "undated")} · {row.call_label}
            {row.source === "echo" ? " · scored" : " · by hand"}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                        color: scoreColor(row.average) }}>
            {row.average}
          </div>
          {/* The denominator travels with the number here as everywhere else:
              an average over two observed parameters and one over eight are
              not the same claim. */}
          <div style={{ fontSize: 9.5, ...muted }}>over {row.observed_count} scored</div>
          {/* A rep with only a thin call this week (few parameters observed —
              a short call, most often) still needs a row, but the server only
              ranks a thin call above a substantial one when nothing else
              exists. Said here so a manager doesn't read "2.1" as a real
              full-call average. */}
          {row.thin && (
            <div style={{ fontSize: 9.5, color: "var(--c-500)" }} title="Only a short call this week">
              thin sample
            </div>
          )}
        </div>
      </div>

      {row.headline && (
        <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--c-800)" }}>{row.headline}</p>
      )}

      {focus && (
        <div style={{ marginTop: 7, paddingLeft: 10, borderLeft: "2px solid var(--c-300)" }}>
          <div style={{ fontSize: 11, ...muted, textTransform: "uppercase", letterSpacing: ".04em" }}>
            Talk about — {focus.label}{" "}
            <strong style={{ color: scoreColor(focus.score) }}>{focus.score}/10</strong>
          </div>
          {focus.heard && (
            <blockquote style={{ margin: "4px 0 3px", fontSize: 12.5, fontStyle: "italic",
                                 color: "var(--c-800)", lineHeight: 1.5 }}>
              “{focus.heard}”
              {focus.at_seconds != null && (
                <span style={{ fontStyle: "normal", marginLeft: 8, whiteSpace: "nowrap" }}>
                  {href && isPlayable(row.call_link) ? (
                    <a href={href} target="_blank" rel="noreferrer"
                       style={{ ...muted, fontSize: 11, textDecoration: "none",
                                display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <Play size={10} /> {stamp(focus.at_seconds)}
                    </a>
                  ) : (
                    <span style={{ ...muted, fontSize: 11 }}>▸ {stamp(focus.at_seconds)}</span>
                  )}
                </span>
              )}
            </blockquote>
          )}
          {focus.why && (
            <p style={{ margin: "0 0 4px", fontSize: 12, color: "var(--c-700)" }}>{focus.why}</p>
          )}
          {/* Only the `ten` anchor, and only as a target. The `zero` anchors are
              written to define a standard and read as an insult when aimed at a
              named person — there is deliberately no path here that shows one. */}
          {focus.ten && (
            <div style={{ fontSize: 11.5, color: "var(--c-700)" }}>
              <span style={{ ...muted }}>What a 10 sounds like: </span>{focus.ten}
            </div>
          )}
          {focus.try_next && (
            <div style={{ fontSize: 12, color: "var(--c-800)", marginTop: 3 }}>
              → {focus.try_next}
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={() => onOpen && onOpen(row)}
              style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 5,
                       border: "1px solid var(--c-200)", background: "transparent",
                       borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                       fontSize: 11.5, fontWeight: 600, color: "var(--blue-600)" }}>
        {focus?.at_seconds != null ? `Open this call at ${stamp(focus.at_seconds)}` : "Open this call"}
        <ArrowRight size={12} />
      </button>
    </div>
  );
}

// A rep's dispute, answered here. The coach sees the score, the evidence it
// rests on and the rep's own reason, and either keeps the score or changes it
// -- with a note either way, because the rep reads the answer on their card.
function DisputeCard({ dispute, onResolved, onOpen }) {
  const s = dispute.call_summary || {};
  const [note, setNote] = useState("");
  const [score, setScore] = useState(String(dispute.score_at_dispute ?? ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const resolve = async (outcome) => {
    setBusy(true);
    setError("");
    try {
      await apiPost(`/RevOps/ScoreDisputes/${dispute.id}/resolve/`, {
        outcome, note, score: outcome === "adjusted" ? (score === "" ? null : Number(score)) : undefined,
      });
      onResolved && onResolved();
    } catch (err) {
      setError(err?.message || "That didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  const button = {
    border: "1px solid var(--c-200)", background: "transparent", borderRadius: 8, padding: "5px 10px",
    cursor: "pointer", fontSize: 11.5, fontWeight: 600, fontFamily: "inherit",
  };
  return (
    <div style={{ padding: "12px 0", borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <strong style={{ fontSize: 13.5, color: "var(--c-900)" }}>{dispute.employee_name}</strong>
          <span style={{ fontSize: 11.5, ...muted }}>
            {" "}disputes <strong style={{ color: "var(--c-700)" }}>{dispute.parameter_label}</strong>
            {" "}· {s.company_name || s.call_label} · {shortDate(s.call_date, "undated")}
          </span>
        </div>
        <strong style={{ fontSize: 15, color: scoreColor(s.current_score) }}>
          {s.current_score == null ? "—" : `${s.current_score}/10`}
        </strong>
      </div>
      {s.heard && (
        <div style={{ marginTop: 4, fontSize: 12.5, fontStyle: "italic", color: "var(--c-800)" }}>
          {s.quote_speaker
            ? `${s.quote_speaker.charAt(0).toUpperCase()}${s.quote_speaker.slice(1)} said `
            : ""}“{s.heard}”
          {s.at_seconds != null && <span style={{ ...muted, fontStyle: "normal", marginLeft: 6 }}>▸ {stamp(s.at_seconds)}</span>}
        </div>
      )}
      {s.why && <div style={{ marginTop: 2, fontSize: 12, color: "var(--c-700)" }}>Scored because: {s.why}</div>}
      <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: "var(--c-100, rgba(120,120,120,.08))",
                    fontSize: 12.5, color: "var(--c-800)" }}>
        <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", marginRight: 6 }}>
          Their reason
        </span>
        {dispute.reason}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Your answer. The rep reads this on their card."
                style={{ marginTop: 8, width: "100%", fontSize: 12.5, padding: "6px 8px", borderRadius: 7,
                         border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)",
                         fontFamily: "inherit", resize: "vertical" }} />
      {error && <div style={{ fontSize: 11.5, color: "var(--red-600, #c0392b)" }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
        <button type="button" disabled={busy || !note.trim()} onClick={() => resolve("upheld")}
                style={{ ...button, color: "var(--c-800)", opacity: busy || !note.trim() ? 0.5 : 1 }}>
          Keep {s.current_score ?? "the score"}{s.current_score != null ? "/10" : ""}
        </button>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <select value={score} onChange={(e) => setScore(e.target.value)}
                  style={{ padding: "4px 6px", borderRadius: 7, border: "1px solid var(--c-200)",
                           background: "var(--surface)", color: "var(--c-900)", fontSize: 12 }}>
            {[...Array(11).keys()].map((n) => <option key={n} value={String(n)}>{n}</option>)}
            <option value="">Not seen</option>
          </select>
          <button type="button" disabled={busy || !note.trim()} onClick={() => resolve("adjusted")}
                  style={{ ...button, color: "var(--blue-600)", opacity: busy || !note.trim() ? 0.5 : 1 }}>
            Change the score
          </button>
        </span>
        <button type="button" onClick={() => onOpen && onOpen(dispute)}
                style={{ ...button, border: "none", color: "var(--blue-600)", display: "inline-flex",
                         alignItems: "center", gap: 4 }}>
          Open the call <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

export default function ReviewQueue({ onOpenEmployee }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [windowDays, setWindowDays] = useState(7);
  const [disputes, setDisputes] = useState([]);
  const [disputeTick, setDisputeTick] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [disputesFailed, setDisputesFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDisputesFailed(false);
    apiGet("/RevOps/ScoreDisputes/?status=open")
      .then((payload) => !cancelled && setDisputes(unpackList(payload)))
      // Not "no disputes": a coach who cannot see the list must be told, or a dispute goes unanswered.
      .catch(() => !cancelled && setDisputesFailed(true));
    return () => { cancelled = true; };
  }, [disputeTick]);

  // A new window starts blank; a refresh after a dispute is settled keeps what is on screen.
  useEffect(() => { setData(null); }, [windowDays]);

  // Settling a dispute can change a score, so the queue is read again each time one is settled.
  useEffect(() => {
    let cancelled = false;
    setError("");
    apiGet(`/RevOps/review-queue/?window=${windowDays}`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load the review queue."));
    return () => { cancelled = true; };
  }, [windowDays, disputeTick, attempt]);

  const queue = data?.queue || [];
  const quiet = data?.no_calls_this_window || [];

  // The window buttons are drawn in every state, so one failed request is never a dead end.
  const windowBar = (
    <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 12,
                  alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                       fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                       color: "var(--c-600)" }}>
          <ClipboardList size={13} /> This week's reviews
        </span>
        <div style={{ fontSize: 11.5, ...muted, marginTop: 3 }}>
          The lowest-scoring call per rep, worst first — one each, ready to open in a 1:1.
        </div>
      </div>
      <div style={{ display: "flex", gap: 3 }}>
        {[7, 14, 30].map((d) => (
          <button key={d} onClick={() => setWindowDays(d)} style={{
            border: "none", background: windowDays === d ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
            cursor: "pointer", fontSize: 11.5, padding: "4px 9px", borderRadius: 7,
            fontWeight: windowDays === d ? 700 : 400,
            color: windowDays === d ? "var(--blue-600)" : "var(--c-500)",
          }}>{d}d</button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {windowBar}

      {disputesFailed && (
        <LoadFailed message="Couldn't load the disputes waiting for you." onRetry={() => setDisputeTick((t) => t + 1)} />
      )}

      {disputes.length > 0 && (
        <div style={{ ...card, borderColor: "var(--blue-600)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                         fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                         color: "var(--blue-600)" }}>
            <Flag size={13} /> Disputes waiting for you ({disputes.length})
          </span>
          <div style={{ fontSize: 11.5, ...muted, marginTop: 3 }}>
            A rep thinks a score is wrong. Look at the moment, then keep it or change it.
          </div>
          {disputes.map((d) => (
            <DisputeCard key={d.id} dispute={d} onResolved={() => setDisputeTick((t) => t + 1)}
                         onOpen={(x) => onOpenEmployee && onOpenEmployee({
                           employee_id: x.employee, call_id: x.call,
                           focus: { at_seconds: x.call_summary?.at_seconds ?? null },
                         })} />
          ))}
        </div>
      )}

      {error ? (
        <LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !data ? (
        <p style={muted}>Loading…</p>
      ) : (
      <>
      <div style={card}>
        {queue.length === 0 ? (
          <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>
            No calls scored in the last {data.window_days} days, so there is nothing to review.
            That is not the same as everybody doing well — check the section below.
          </p>
        ) : (
          queue.map((row) => (
            <QueueRow key={row.call_id} row={row} onOpen={onOpenEmployee} />
          ))
        )}
      </div>

      {quiet.length > 0 && (
        <div style={{ ...card, borderStyle: "dashed" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                         fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                         color: "var(--c-600)" }}>
            <UserX size={13} /> No scored calls at all this window
          </span>
          <p style={{ margin: "5px 0 8px", fontSize: 12, ...muted }}>
            These people are on a track and have nothing in the last {data.window_days} days.
            Going quiet looks identical to improving on every other screen, so it gets named here
            rather than left as an absence.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {quiet.map((p) => (
              <button key={p.employee_id} type="button"
                      onClick={() => onOpenEmployee && onOpenEmployee(p)}
                      style={{ border: "1px solid var(--c-200)", background: "transparent",
                               borderRadius: 999, padding: "4px 11px", cursor: "pointer",
                               fontSize: 12, color: "var(--c-800)" }}>
                {p.employee_name} <span style={muted}>· {p.role}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
